import * as Sentry from "@sentry/react";
import { AlertTriangle, RotateCcw, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router";

interface ErrorElementProps {
  error: unknown;
  resetError: () => void;
  componentStack?: string;
}

export const ErrorElement = ({
  error,
  resetError,
  componentStack,
}: ErrorElementProps) => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate("/");
  };

  // Ensure error is Error-like
  const errorInfo = error instanceof Error ? error : new Error(String(error));

  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center">
      <div className="flex items-center justify-center w-16 h-16 mb-4 rounded-full bg-red-100 dark:bg-red-900/20">
        <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" />
      </div>
      <h2 className="mb-2 text-xl font-semibold text-gray-900 dark:text-gray-100">
        Something went wrong
      </h2>
      <p className="mb-6 text-gray-600 dark:text-gray-400 max-w-md">
        We've encountered an unexpected error. Our team has been notified and is
        working on a fix.
      </p>
      <div className="flex gap-3">
        <Button onClick={resetError} variant="outline" className="gap-2">
          <RotateCcw className="w-4 h-4" />
          Try again
        </Button>
        <Button onClick={handleGoHome} className="gap-2">
          <Home className="w-4 h-4" />
          Go to Dashboard
        </Button>
      </div>
      {import.meta.env.DEV && (
        <details className="mt-6 p-4 bg-gray-100 dark:bg-gray-800 rounded text-left max-w-2xl">
          <summary className="cursor-pointer font-medium mb-2">
            Error Details
          </summary>
          <pre className="text-sm text-red-600 dark:text-red-400 whitespace-pre-wrap">
            {errorInfo.message}
            {"\n\n"}
            {errorInfo.stack}
            {componentStack && "\n\nComponent Stack:\n"}
            {componentStack}
          </pre>
        </details>
      )}
    </div>
  );
};

export const ErrorBoundary = Sentry.withErrorBoundary(
  ({ children }: { children: React.ReactNode }) => <>{children}</>,
  {
    fallback: ErrorElement,
    showDialog: false,
    beforeCapture: (scope) => {
      scope.setTag("errorBoundary", true);
      scope.setContext("errorBoundary", {
        errorBoundary: "ErrorBoundary",
        timestamp: new Date().toISOString(),
      });
    },
  }
);
